import axios from 'axios';
import logger from '../utils/logger';

// Create axios instance with default config
const api = axios.create({
    baseURL: process.env.REACT_APP_API_URL,
    timeout: 10000,
    headers: {
        'Content-Type': 'application/json'
    }
});

// Add request interceptor for logging and auth
api.interceptors.request.use(
    (config) => {
        logger.debug('API Request', {
            url: config.url,
            method: config.method,
            stage: process.env.REACT_APP_STAGE
        });

        // Add auth token if available
        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }

        return config;
    },
    (error) => {
        logger.error('API Request Error', error);
        return Promise.reject(error);
    }
);

// Update response interceptor for better error handling
api.interceptors.response.use(
    (response) => {
        logger.debug('API Response', {
            url: response.config.url,
            status: response.status,
            stage: process.env.REACT_APP_STAGE
        });
        return response;
    },
    (error) => {
        // Enhanced error logging
        logger.error('API Response Error', {
            url: error.config?.url,
            status: error.response?.status,
            message: error.message,
            data: error.response?.data,
            stage: process.env.REACT_APP_STAGE
        });

        // Handle session expiration
        if (error.response?.status === 401 && !error.config.url.includes('/login')) {
            localStorage.clear();
            window.location.href = '/login';
            return Promise.reject(new Error('Session expired. Please login again.'));
        }

        // Handle login errors separately
        if (error.config.url.includes('/login')) {
            return Promise.reject(error.response?.data || { message: 'Invalid credentials' });
        }

        return Promise.reject(error.response?.data || error);
    }
);

/**
 * Generic API request handler with consistent error handling
 * @param {Function} apiCall - Axios API call function
 * @returns {Promise} - Promise with the response data or error
 */
const handleApiRequest = async (apiCall) => {
    try {
        const response = await apiCall();
        return response.data;
    } catch (error) {
        throw error;
    }
};

/**
 * Authentication API endpoints
 */
export const authApi = {
    /**
     * Login user
     * @param {Object} credentials - User credentials
     * @param {string} credentials.email - User email
     * @param {string} credentials.password - User password
     * @returns {Promise} - User data including token
     */
    login: (credentials) => handleApiRequest(() => api.post('/login', credentials)),

    /**
     * Register a new user
     * @param {Object} userData - User registration data
     * @returns {Promise} - New user data
     */
    register: (userData) => handleApiRequest(() => api.post('/register', userData)),

    /**
     * Verify email address
     * @param {string} token - Verification token
     * @param {string} email - User email
     * @returns {Promise} - Verification result
     */
    verifyEmail: (token, email) => handleApiRequest(() => api.get(`/verify-email?token=${encodeURIComponent(token)}&email=${encodeURIComponent(email)}`)),

    /**
     * Resend verification email
     * @param {Object} data - Request data
     * @param {string} data.email - User email
     * @returns {Promise} - Response message
     */
    resendVerification: (data) => handleApiRequest(() => api.post('/resend-verification', data)),

    /**
     * Request password reset
     * @param {Object} data - Request data
     * @param {string} data.email - User email
     * @returns {Promise} - Response message
     */
    forgotPassword: (data) => handleApiRequest(() => api.post('/forgot-password', data)),

    /**
     * Reset password with token
     * @param {Object} data - Reset data
     * @param {string} data.email - User email
     * @param {string} data.token - Reset token
     * @param {string} data.newPassword - New password
     * @returns {Promise} - Response message
     */
    resetPassword: (data) => handleApiRequest(() => api.post('/reset-password', data))
};

/**
 * Profile API endpoints
 */
export const profileApi = {
    /**
     * Get user profile data - NOT SURE THIS IS IN USE EITHER BUT WILL NEED UPDATING?!?!?!?
     * @param {string} email - User email
     * @returns {Promise} - User profile data
     */
    //getProfile: (email) => handleApiRequest(() => api.get(`/profile/${email}`)),

    /**
     * Update user profile
     * @param {string} userId - userId
     * @param {Object} profileData - Profile data to update
     * @returns {Promise} - Updated profile data
     */
    updateProfile: (userId, profileData) => handleApiRequest(() => api.put(`/profile/${userId}`, profileData))
};

/**
 * Pool API endpoints
 */
export const poolApi = {
    /**
     * Get pools for a user or all pools
     * @param {string} [userId] - User ID (optional, get all pools if not provided)
     * @returns {Promise} - List of pools
     */
    getPools: (userId) => {
        if (userId) {
            return handleApiRequest(() => api.get(`/pools/${userId}`));
        } else {
            return handleApiRequest(() => api.get(`/pools`));
        }
    },

    /**
     * Get pool details
     * @param {string} locationString - Encoded location string (PK)
     * @param {string} poolName - Encoded pool name (SK)
     * @returns {Promise} - Pool details
     */
    getPoolDetails: (locationString, poolName) =>
        handleApiRequest(() =>
            api.get(`/pools/details/${encodeURIComponent(poolName)}?location=${encodeURIComponent(locationString)}`)
        ),

    /**
     * Add a new pool
     * @param {Object} poolData - Pool data
     * @returns {Promise} - New pool data
     */
    addPool: (poolData) => handleApiRequest(() => api.post('/pools/add', poolData)),

    /**
     * Update pool data
     * @param {string} locationString - Encoded location string (PK)
     * @param {string} poolName - Encoded pool name (SK)
     * @param {Object} updates - Pool data to update
     * @returns {Promise} - Updated pool data
     */
    updatePool: (locationString, poolName, updates) => handleApiRequest(() => api.put(`/pools/details/${encodeURIComponent(poolName)}?location=${encodeURIComponent(locationString)}`, updates)),

    /**
     * Delete a pool
     * @param {string} locationString - Encoded location string (PK)
     * @param {string} poolName - Encoded pool name (SK)
     * @returns {Promise} - Confirmation of deletion
     */
    deletePool: (locationString, poolName) => handleApiRequest(() => api.delete(`/pools/delete/${encodeURIComponent(poolName)}?location=${encodeURIComponent(locationString)}`))
};

/**
 * Rankings API endpoints
 */
export const rankingsApi = {
    /**
     * Get player rankings (paginated, by type)
     * @param {string} type - 'singles' or 'doubles'
     * @param {string} location - Location (country#administrative_area_level_1...) for filtering
     * @param {object} [lastEvaluatedKey] - Optional lastEvaluatedKey for pagination
     * @returns {Promise} - Player rankings data
     */
    getRankings: async (type, location, lastEvaluatedKey = null) => {
        let url = `/rankings/${type}?location=${encodeURIComponent(location)}`;
        if (lastEvaluatedKey) {
            url += `&lastEvaluatedKey=${encodeURIComponent(JSON.stringify(lastEvaluatedKey))}`;
        }
        const response = await handleApiRequest(() => api.get(url));
        return response;
    }
};

/**
 * Matches API endpoints
 */
export const matchesApi = {
    /**
     * Get matches for a user
     * @param {string} email - User email
     * @returns {Promise} - User matches
     */
    //getMyMatches: (email) => handleApiRequest(() => api.get(`/matches/${email}`)),

    /**
     * Get all singles matches, optionally filtered by userId
     * @param {number} [userId] - Optional userId to filter matches
     * @param {object} [lastEvaluatedKey] - Optional lastEvaluatedKey for pagination
     * @param {string} location - Location (country#administrative_area_level_1...) for filtering
     * @param {string} [startTimestamp] - Optional start timestamp for filtering
     * @param {string} [endTimestamp] - Optional end timestamp for filtering
     * @returns {Promise} - All matches or matches for specific user
     */
    getSinglesMatches: (userId = null, lastEvaluatedKey = null, location, startTimestamp, endTimestamp) => {
        // Support location as string or array
        let url = userId
            ? `/matches/singles/${userId}`
            : `/matches/singles`;
        let params = [];
        if (Array.isArray(location)) {
            location.forEach(loc => params.push(`location=${encodeURIComponent(loc)}`));
        } else if (location) {
            params.push(`location=${encodeURIComponent(location)}`);
        }
        if (lastEvaluatedKey) {
            params.push(`lastEvaluatedKey=${encodeURIComponent(JSON.stringify(lastEvaluatedKey))}`);
        }
        if (startTimestamp) {
            params.push(`startTimestamp=${encodeURIComponent(startTimestamp)}`);
        }
        if (endTimestamp) {
            params.push(`endTimestamp=${encodeURIComponent(endTimestamp)}`);
        }
        if (params.length > 0) {
            url += `?${params.join('&')}`;
        }
        console.log("url", url);
        return handleApiRequest(() => api.get(url));
    },

    /**
     * Get all doubles matches, optionally filtered by userId
     * @param {number} [userId] - Optional userId to filter matches
     * @param {object} [lastEvaluatedKey] - Optional lastEvaluatedKey for pagination
     * @param {string} location - Location (country#administrative_area_level_1...) for filtering
     * @param {string} [startTimestamp] - Optional start timestamp for filtering
     * @param {string} [endTimestamp] - Optional end timestamp for filtering
     * @returns {Promise} - All matches or matches for specific user
     */
    getDoublesMatches: (userId = null, lastEvaluatedKey = null, location, startTimestamp, endTimestamp) => {
        let url = userId
            ? `/matches/doubles/${userId}`
            : `/matches/doubles`;
        let params = [];
        if (Array.isArray(location)) {
            location.forEach(loc => params.push(`location=${encodeURIComponent(loc)}`));
        } else if (location) {
            params.push(`location=${encodeURIComponent(location)}`);
        }
        if (lastEvaluatedKey) {
            params.push(`lastEvaluatedKey=${encodeURIComponent(JSON.stringify(lastEvaluatedKey))}`);
        }
        if (startTimestamp) {
            params.push(`startTimestamp=${encodeURIComponent(startTimestamp)}`);
        }
        if (endTimestamp) {
            params.push(`endTimestamp=${encodeURIComponent(endTimestamp)}`);
        }
        if (params.length > 0) {
            url += `?${params.join('&')}`;
        }
        return handleApiRequest(() => api.get(url));
    },

    /**
     * Submit match score
     * @param {Object} matchData - Match data including location
     * @returns {Promise} - Submitted match data
     */
    submitMatchScore: (matchData) => handleApiRequest(() => api.post('/matches/submit', matchData)),

    /**
     * Confirm a match
     * @param {number} userId - UserID
     * @param {number} matchTimestamp - Match timestamp
     * @param {string} matchType - Type of match ('singles' or 'doubles')
     * @param {string} location - Location
     * @returns {Promise} - Confirmed match data
     */
    confirmMatch: (userId, matchTimestamp, matchType, location) => handleApiRequest(() => api.post('/matches/confirm', { userId, matchTimestamp, matchType, location })),

    /**
     * Reject a match
     * @param {number} userId - User email
     * @param {number} matchTimestamp - Match timestamp
     * @param {string} matchType - Type of match ('singles' or 'doubles')
     * @param {string} location - Location
     * @returns {Promise} - Confirmation of rejection
     */
    rejectMatch: (userId, matchTimestamp, matchType, location) => handleApiRequest(() => api.post('/matches/reject', { userId, matchTimestamp, matchType, location }))
};

/**
 * Locations API endpoints
 */
export const locationsApi = {
    /**
     * Get all locations
     * @returns {Promise} - Structured locations data
     */
    getLocations: () => handleApiRequest(() => api.get('/locations'))
};

/**
 * Results API endpoints
 */
export const resultsApi = {
    /**
     * Get results summary
     * @param {string} [location] - Optional location string for filtering
     * @param {number} [startTimestamp] - Optional start timestamp for date range
     * @param {number} [endTimestamp] - Optional end timestamp for date range
     * @returns {Promise} - Results summary data
     */
    getResults: (location = null, startTimestamp = null, endTimestamp = null) => {
        let url = '/results';
        let params = [];
        
        if (location) {
            params.push(`location=${encodeURIComponent(location)}`);
        }
        if (startTimestamp) {
            params.push(`startTimestamp=${encodeURIComponent(startTimestamp)}`);
        }
        if (endTimestamp) {
            params.push(`endTimestamp=${encodeURIComponent(endTimestamp)}`);
        }
        
        if (params.length > 0) {
            url += `?${params.join('&')}`;
        }
        
        return handleApiRequest(() => api.get(url));
    }
};

/**
 * Ads API endpoints
 */
export const adsApi = {
    /**
     * Fetch advertisements for a location
     * @param {string} location - User location
     * @returns {Promise} - Advertisement data
     */
    fetchAds: (location) => handleApiRequest(() => api.get(`/ads/${location}`))
};

/**
 * User Search API endpoint
 */
export const searchUser = (country, query) => handleApiRequest(() => api.get(`/user-search?country=${encodeURIComponent(country)}&query=${encodeURIComponent(query)}`));

// Export individual functions for backward compatibility
export const {
    login: loginUser,
    register: registerUser
} = authApi;

export const {
    //getProfile,
    updateProfile
} = profileApi;

export const {
    getPools,
    getPoolDetails,
    addPool,
    updatePool,
    deletePool
} = poolApi;

export const {
    getRankings
} = rankingsApi;

export const {
    getDoublesMatches,
    getSinglesMatches,
    submitMatchScore,
    confirmMatch,
    rejectMatch
} = matchesApi;

export const {
    getResults
} = resultsApi;

export const {
    getLocations
} = locationsApi;

export const {
    fetchAds
} = adsApi;

export default api;